import { Box, CircularProgress, Paper, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Layout, LoginLogoImg, useBaseStore } from '@roc/feature-app-core';
import { createSecureLinkClient } from '@roc-capital/secure-link';
import type { SecureLinkPurpose } from '@roc-capital/secure-link';
import { useSecureLinkSession } from '@roc-capital/secure-link/react';
import { Button, StepObj, StepsContainer } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useStore } from '@roc/client-portal-shared/hooks';
import { BankInfoStep } from './bankInfoStep';
import { SignatureStep } from './signatureStep';
import { SummaryStep } from './summaryStep';
import { WelcomeStep } from './welcomeStep';
import { WireFraudNoticeStep } from './wireFraudNoticeStep';

const useStyles = makeStyles(theme =>
  createStyles({
    stepper: {
      maxWidth: 1000,
      margin: 'auto',
      '& .MuiStepLabel-completed': {
        color: theme.palette.primary.main,
      },
    },
    paper: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
    },
  })
);

const WIRE_INSTRUCTIONS: SecureLinkPurpose = 'WIRE_INSTRUCTIONS';

const Message = ({ title, text, action }: { title: string; text: string; action?: { label: string; onClick: () => void } }) => (
  <Box textAlign="center" py={6} px={2} maxWidth={520} mx="auto">
    <Typography variant="h5" gutterBottom>
      {title}
    </Typography>
    <Typography color="textSecondary">{text}</Typography>
    {action && (
      <Box mt={3}>
        <Button testId="retry" variant="outlined" color="primary" onClick={action.onClick}>
          {action.label}
        </Button>
      </Box>
    )}
  </Box>
);

/**
 * The wire instructions form for secure links: /link/wire-instructions. The title company arrives here from the platform
 * landing page (/platform-app/link#token), which started the session; every call is authorized by that session, and the
 * loan comes from it on the server. Replaces /wire-instructions/:token, which carried an HMAC token in the URL.
 */
export const WireInstructionsPage = observer(() => {
  const { environmentStore } = useBaseStore();
  const client = useMemo(
    () => createSecureLinkClient({ apiBaseUrl: environmentStore.environmentVariables.restApiBaseURL ?? '' }),
    []
  );
  const { status, request, retry } = useSecureLinkSession(client, { purpose: WIRE_INSTRUCTIONS });
  const { wireInstructionsStore } = useStore() as any;
  const store = wireInstructionsStore;

  // A layout effect, so the store has the session before the form's first data fetch on the same commit.
  useLayoutEffect(() => store.setSession(request), [store, request]);

  return (
    <Layout maxWidth="lg">
      <Box style={{ display: 'flex', justifyContent: 'center' }}>
        <LoginLogoImg imgStyle={{ width: '150px' }} />
      </Box>
      {status === 'loading' && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}
      {status === 'ended' && (
        <Message
          title="Your session has ended"
          text="For your security, sessions end after 15 minutes or when the link is opened in another window. Open the link again to continue."
        />
      )}
      {status === 'unavailable' && (
        <Message
          title="We couldn't open your form"
          text="This is usually temporary. Check your connection and try again."
          action={{ label: 'Try again', onClick: retry }}
        />
      )}
      {status === 'active' && <WireInstructionsSteps store={store} />}
    </Layout>
  );
});

/**
 * /wire-instructions/:token, the old wire instructions link format (an HMAC token in the URL). Those links no longer
 * work: the lender sends a new one, which opens /link/wire-instructions.
 */
export const WireInstructionsLinkReplacedPage = () => (
  <Layout maxWidth="lg">
    <Box style={{ display: 'flex', justifyContent: 'center' }}>
      <LoginLogoImg imgStyle={{ width: '150px' }} />
    </Box>
    <Message
      title="This link is no longer valid"
      text="Please ask your lender to send you a new link to submit your wire instructions."
    />
  </Layout>
);

const WireInstructionsSteps = observer(({ store }: { store: any }) => {
  const [showWelcome, setShowWelcome] = useState(true);
  const classes = useStyles();

  useEffect(() => {
    store.fetchData();
  }, [store]);

  // Once data is loaded, skip the welcome screen if the form was already
  // submitted in a previous visit (the store lands on the summary step).
  useEffect(() => {
    if (store.loanData && store.submitted) {
      setShowWelcome(false);
    }
  }, [store.loanData, store.submitted]);

  const steps: StepObj[] = [
    {
      title: 'Wire Fraud Notice',
      icon: null,
      completed: store.activeStep > 0,
    },
    {
      title: 'Bank Information',
      icon: null,
      completed: store.activeStep > 1,
    },
    {
      title: 'DocuSign Confirmation',
      icon: null,
      completed: store.activeStep > 2,
    },
    {
      title: 'Summary',
      icon: null,
      allowBack: false,
    },
  ] as unknown as StepObj[];

  return (
    <>
      {showWelcome ? (
        <WelcomeStep store={store} onStart={() => setShowWelcome(false)} />
      ) : (
        <Paper className={classes.paper}>
          <StepsContainer
            disableNavigation={true}
            activeStep={store.activeStep}
            handleStep={step => store.setActiveStep(step)}
            steps={steps}
            stepperClassName={classes.stepper}
          >
            <WireFraudNoticeStep store={store} onContinue={() => store.setActiveStep(1)} />
            <BankInfoStep store={store} onContinue={() => store.setActiveStep(2)} />
            <SignatureStep store={store} />
            <SummaryStep store={store} />
          </StepsContainer>
        </Paper>
      )}
    </>
  );
});
